import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { RootState } from '../store';
import { clearApiError } from '../store/slices/apiError.slice';
import { ErrorSource } from '../constant/model/api/api';
import { usePartnersQuery } from '../queries/usePartnersQuery';
import { partnerKeys } from '../constant/query-keys/partnerKeys';

export const usePartners = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const apiErrors = useSelector((state: RootState) => state.apiError?.errors || {});
  
  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isFetching: isFetchingPartners,
    error: partnersError,
    refetch: refetchPartners
  } = usePartnersQuery();

  const clearPartnerApiError = useCallback((source: ErrorSource) => {
    dispatch(clearApiError(source));
  }, [dispatch]);

  const clearAllPartnerApiErrors = useCallback(() => {
    dispatch(clearApiError(ErrorSource.PARTNER_LIST));
    dispatch(clearApiError(ErrorSource.PARTNER_DETAILS));
  }, [dispatch]);


  const invalidatePartnersCache = useCallback(() => {
    queryClient.invalidateQueries(partnerKeys.lists());
  }, [queryClient]);

  const getApiErrorBySource = useCallback((source: ErrorSource) => {
    return apiErrors[source];
  }, [apiErrors]);
  
  const hasApiError = useCallback((source: ErrorSource) => {
    return !!apiErrors[source];
  }, [apiErrors]);
  
  const apiErrorContains = useCallback((source: ErrorSource, text: string) => {
    const error = apiErrors[source];
    return !!error && error.message.includes(text);
  }, [apiErrors]);

  return {
    partners: partnersData?.partners || [],
    isLoadingPartners,
    isFetchingPartners,
    partnersError,
    refetchPartners,
    
    invalidatePartnersCache,
    
    clearApiError: clearPartnerApiError,
    clearAllApiErrors: clearAllPartnerApiErrors,
    
    getApiErrorBySource,
    hasApiError,
    apiErrorContains
  };
};